export const PASSPORT_COOKIE_KEY = 'passport_v1'

/**
 * Route name of the login page. Kept here so the gate middleware, the login page
 * and the header all agree on one value.
 */
export const LOGIN_ROUTE_NAME = 'login'

/**
 * Narrow an untrusted `?redirect=` value to a same-origin path.
 *
 * Rejects absolute URLs, protocol-relative `//evil.com` and the `/\evil.com`
 * variant that some browsers normalise into a host — i.e. open redirects.
 */
export function safeRedirect(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined
  if (!value.startsWith('/')) return undefined
  if (value.startsWith('//') || value.startsWith('/\\')) return undefined
  return value
}

/** Lifetime of the passport cookie — matched to the refresh token (30 days). */
export const PASSPORT_MAX_AGE = 60 * 60 * 24 * 30

/**
 * The single definition of how the passport cookie is written.
 *
 * Both writers must use it: `server/middleware/token-login.ts` (server, `setCookie`)
 * and `app/stores/useCustomerStore.ts` (client, `useCookie`). They used to carry
 * separate option objects and the client one omitted `secure` and `maxAge`, so the
 * first `refresh()` silently rewrote a 30-day Secure cookie as a non-secure session
 * cookie — "stay logged in" ended at the next browser restart.
 *
 * NOT httpOnly on purpose: the client store reads the token out of it to build the
 * Authorization header.
 */
export function passportCookieOptions() {
  return {
    path: '/',
    sameSite: 'lax' as const,
    secure: process.env.NODE_ENV === 'production',
    maxAge: PASSPORT_MAX_AGE,
  }
}

/**
 * Best-effort "has this access token expired?" check, used to refresh a session
 * BEFORE a page renders instead of discovering it through a failed request.
 *
 * Deliberately does NOT verify the signature: `JWT_SECRET` never reaches the
 * frontend (see AUTH.md), and it does not have to — the backend re-verifies every
 * call, so this is only a hint about when to spend a refresh round-trip. An
 * unparseable or `exp`-less token counts as expired: the refresh that follows is
 * the cheapest way to find out whether the session is still real.
 *
 * `skewSeconds` covers the gap between the middleware running and the request
 * actually going out, so a token that dies in between does not slip through.
 */
export function accessTokenExpired(token: string | null | undefined, skewSeconds = 30): boolean {
  if (!token) return true

  const payload = token.split('.')[1]
  if (!payload) return true

  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = (JSON.parse(json) as { exp?: number }).exp
    if (typeof exp !== 'number') return true
    return Date.now() >= (exp - skewSeconds) * 1000
  } catch {
    return true
  }
}
